import {useDiffEntriesDigestsWithFilter, useGetSelectedPathDigest} from '../stores/selectors'
import {updateURLHash} from '@github-ui/diffs/document-hash-helpers'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useCallback} from 'react'

type UseDiffNavigation = {
  /** Function to navigate to the previous file */
  navigateToPrevious: () => void
  /** Function to navigate to the next file */
  navigateToNext: () => void
}

/**
 * Hook that provides navigation functionality for navigating files in a pull request.
 *
 * Navigation should only operate on files that are visible in the file tree. The
 * store exposes both the full progressive entries and a filtered list derived from
 * the current file filter state; prefer the filtered list so "Next/Previous" skips
 * hidden files.
 *
 * @returns UseDiffNavigation object containing navigation functions and state
 */
export function useDiffNavigation(): UseDiffNavigation {
  // Get the filtered summaries and use the specialized selector to obtain the
  // progressive entries that correspond to the currently visible files.
  const diffEntries = useDiffEntriesDigestsWithFilter()
  const getSelectedPathDigest = useGetSelectedPathDigest()

  const navigate = useCallback((pathDigest: string) => {
    updateURLHash(`diff-${pathDigest}`)
    addUrlToHistoryStack(window.location.href)
  }, [])

  const navigateToPrevious = useCallback(() => {
    if (diffEntries.length > 0) {
      const selectedPathDigest = getSelectedPathDigest()
      const currentIndex = selectedPathDigest ? diffEntries.indexOf(selectedPathDigest) : -1

      let previousIndex: number
      if (currentIndex <= 0) {
        // Wrap to the last item
        previousIndex = diffEntries.length - 1
      } else {
        previousIndex = currentIndex - 1
      }
      const pathDigest = diffEntries[previousIndex]
      if (pathDigest) {
        navigate(pathDigest)
      }
    }
  }, [diffEntries, getSelectedPathDigest, navigate])

  const navigateToNext = useCallback(() => {
    if (diffEntries.length > 0) {
      let nextIndex: number
      const selectedPathDigest = getSelectedPathDigest()
      const currentIndex = selectedPathDigest ? diffEntries.indexOf(selectedPathDigest) : -1

      if (currentIndex >= diffEntries.length - 1 || currentIndex < 0) {
        // Wrap to the first item
        nextIndex = 0
      } else {
        nextIndex = currentIndex + 1
      }
      const pathDigest = diffEntries[nextIndex]
      if (pathDigest) {
        navigate(pathDigest)
      }
    }
  }, [diffEntries, getSelectedPathDigest, navigate])

  return {
    navigateToPrevious,
    navigateToNext,
  }
}
